import { ObjectId } from 'bson';
import { computed, makeObservable, toJS } from 'mobx';

import { concatPath, moment, TimeFormat } from '@feathr/hooks';
import type { Languages } from '@feathr/locales';
import type { DeepObservable, IBaseAttributes, IRachisMessage, TConstraints } from '@feathr/rachis';
import { Collection, DisplayModel, isWretchError, wretch } from '@feathr/rachis';

import type { Account } from './accounts';
import type { TCampaignGroup } from './campaigns';
import { CampaignClass } from './campaigns';
import type { TFlagsRecord } from './flags';
import type { IConfig } from './redirects';
import Session from './Session';
import type { UserNotification } from './user_notifications';
import { EUserRoleIDs, type IUserRole } from './user_roles';

const reportsClassNames = [...Object.values(CampaignClass), 'Flight', 'Event'] as const;
export type TReportClass = (typeof reportsClassNames)[number];

export interface IUserSettings {
  // Hex code of an ETheme color.
  bee_user_color?: string;
  dark_mode?: boolean;
  /** Project campaign tables, e.g. `Some Project > Marketing > All` */
  displayed_campaign_columns?: Record<TCampaignGroup, string[]>;
  /** Global campaign columns, e.g. `Home > Marketing > All` */
  displayed_campaign_columns_global?: Record<TCampaignGroup, string[]>;
  persons_column_ids?: string[];
  favorite_account_ids?: string[];
  invite_partners_column_ids?: string[];
  partners_column_ids?: string[];
  segments_column_ids?: string[];
  reports_config?: Record<TReportClass, IConfig>;
  lng?: Languages;
  forms_pinned_fields?: Record<Account['id'], string[]>;
  show_feathr_lite_help_card?: boolean;
  /** Only show available campaign types in CampaignTypeSelect */
  only_show_available_options?: boolean;
  show_dev_nav?: boolean;
}

export interface IUser extends IBaseAttributes {
  account: string;
  calendly_link?: string;
  confirmed: boolean;
  date_last_access: string;
  date_last_modified: string;
  email: string;
  first_name?: string;
  flags: TFlagsRecord;
  /**
   * If this field is populated, the referenced UserRole will apply
   * to the user across all accounts. Only used for feathren as of now.
   */
  global_role?: string;
  last_name?: string;
  last_nps: number | null;
  login_attempts: number;
  /** Total number of logins  */
  logins: number;
  name?: string;
  picture?: string;
  recent_account_ids: string[];
  /**
   * Populated for users who either have global_role or are on the
   * currently logged in account's user_roles list. This lets us
   * avoid checking the account for the user's current permissions.
   */
  role?: IUserRole;
  settings: IUserSettings;
  skilljar_id: string;
  timezone: string;
  username?: string;
  email_opt_out?: boolean;
  internal_notification_opt_out?: boolean;

  /**
   * @deprecated Use UserNotifications instead
   */
  notifications?: Array<UserNotification['id']>;
}

export class User extends DisplayModel<IUser> {
  public readonly className = 'User';

  public get constraints(): TConstraints<IUser> {
    return {
      name: {
        presence: {
          allowEmpty: false,
        },
      },
      first_name: {
        presence: {
          allowEmpty: false,
          message: '^First name is required',
        },
      },
      last_name: {
        presence: {
          allowEmpty: false,
          message: '^Last name is required',
        },
      },
      timezone: {
        presence: {
          allowEmpty: false,
        },
      },
      calendly_link: {
        url: {
          allowLocal: false,
          message: '^Please enter a valid URL for your Calendly link',
        },
      },
    };
  }

  constructor(attributes: Partial<IUser> = {}) {
    super(attributes);

    makeObservable(this);
  }

  public getItemUrl(pathSuffix?: string): string {
    const currentUserId = '';
    if (this.id === currentUserId) {
      return concatPath(`/settings/user/profile`, pathSuffix);
    }
    return concatPath(`/settings/account/users/${this.id}`, pathSuffix);
  }

  public override getDefaults(): Partial<IUser> {
    return {
      recent_account_ids: [],
    };
  }

  /**
   * Get reports_config setting - maps report classes to report configs
   */
  public getReportsConfigSetting(
    reportClass: TReportClass,
    initialConfig: IConfig,
  ): Record<TReportClass, IConfig> {
    return this.getSetting('reports_config', {
      [reportClass]: initialConfig,
    } as Record<TReportClass, IConfig>);
  }

  /**
   * Set reports_config setting (forcing settings attribute to be dirty)
   */
  public setReportsConfigSetting(config: Record<TReportClass, IConfig>): void {
    this.setSetting('reports_config', config);
    this.setAttributeDirty('settings');
  }

  public getSetting<K extends keyof IUserSettings & string>(
    attribute: K,
  ): DeepObservable<IUserSettings[K]>;

  public getSetting<K extends keyof IUserSettings & string>(
    attribute: K,
    defaultValue: Exclude<IUserSettings[K], undefined>,
  ): DeepObservable<Exclude<IUserSettings[K], undefined>>;

  public getSetting<K extends keyof IUserSettings & string>(
    key: K,
    defaultValue?: unknown,
  ): unknown {
    const settings: IUserSettings = this.get('settings', {});
    return settings[key] !== undefined && settings[key] !== null ? settings[key] : defaultValue;
  }

  public setSetting<K extends keyof IUserSettings & string>(key: K, value: IUserSettings[K]): void {
    const settings: IUserSettings = this.get('settings', {});
    this.set({ settings: { ...settings, [key]: value } });
  }

  public get flags(): TFlagsRecord {
    /*
     * This getter exists because we only want to consider the truthy keys to override
     * account flags. User flags should be able to set a flag to true, not set it back
     * to false (turn off a feature) if it is already on at the account level.
     */
    if (!this.get('flags')) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(toJS(this.get('flags'))).filter(([, value]) => {
        return value as boolean;
      }),
    );
  }

  @computed
  public get dateCreated(): string {
    return moment.utc(new ObjectId(this.id).getTimestamp()).format(TimeFormat.isoDateTime);
  }

  @computed
  public get isAccountant(): boolean {
    return this.get('role')?.id === EUserRoleIDs.Accountant;
  }

  @computed
  public get isAdmin(): boolean {
    return this.get('role')?.id === EUserRoleIDs.Admin;
  }

  @computed
  public get isInternalOnboarding(): boolean {
    return this.get('role')?.id === EUserRoleIDs.InternalOnboarding;
  }

  @computed
  public get isSudoer(): boolean {
    return this.get('role')?.sudo?.mode === 'all';
  }

  @computed
  public get isSuperuser(): boolean {
    return this.get('role')?.id === EUserRoleIDs.SuperUser;
  }

  @computed
  public get isInternal(): boolean {
    return [
      EUserRoleIDs.SuperUser,
      EUserRoleIDs.AccountManager,
      EUserRoleIDs.InternalOnboarding,
      EUserRoleIDs.Accountant,
    ].includes(this.get('role')?.id as EUserRoleIDs);
  }

  @computed
  public get name(): string {
    return this.get('name', '').trim() || 'Unnamed User';
  }

  @computed
  public get firstName(): string {
    return this.get('first_name', '').trim();
  }

  @computed
  public get lastName(): string {
    return this.get('last_name', '').trim();
  }

  @computed
  public get role(): string {
    return this.get('role')?.id || EUserRoleIDs.User;
  }

  @computed
  public get email(): string {
    return this.get('email', '').trim();
  }

  /**
   * Forcefully resend an email invite to the user.
   */
  public async resendInvite(): Promise<IRachisMessage> {
    this.assertCollection(this.collection);

    const response = await wretch<IRachisMessage>(this.collection.url('invite'), {
      method: 'POST',
      body: JSON.stringify({
        email: this.email,
        role: this.role,
        first_name: this.firstName,
        last_name: this.lastName,
        force: true,
      }),
      headers: this.collection.getHeaders(),
    });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  /**
   * Send a password reset link to the user.
   */
  public async sendPasswordResetLink(): Promise<IRachisMessage> {
    this.assertCollection(this.collection);

    const response = await wretch<IRachisMessage>(this.collection.url('reset-password'), {
      method: 'POST',
      body: JSON.stringify({ email: this.get('email') }),
      headers: { 'Content-Type': 'application/json' },
    });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }
}

export class Users extends Collection<User> {
  public getModel(attributes: Partial<IUser>): User {
    return new User(attributes);
  }

  public getClassName(): string {
    return 'users';
  }

  public override url(): string;

  public override url(variant: 'invite' | 'reset-password'): string;

  public override url(variant?: 'invite' | 'reset-password'): string {
    switch (variant) {
      case 'invite':
        return `${this.getHostname()}invite`;

      case 'reset-password':
        return `${this.getHostname()}reset-password`;

      default:
        return super.url();
    }
  }

  public async invite({
    users,
  }: {
    users: Array<{ first_name: string; last_name: string; email: string; role: string }>;
  }): Promise<IRachisMessage> {
    const response = await wretch<IRachisMessage>(this.url('invite'), {
      method: 'POST',
      body: JSON.stringify({ users }),
      headers: this.getHeaders(),
    });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }
}

export async function fetchUserById(userId: string, includeAccountContext = true): Promise<IUser> {
  const response = await wretch<IUser>(`${BLACKBOX_URL}users/${userId}`, {
    method: 'GET',
    headers: Session.getHeaders(includeAccountContext),
  });

  if (isWretchError(response)) {
    throw response.error;
  }

  return response.data;
}

export interface IUpdateUserPayload {
  first_name?: string;
  last_name?: string;
  name?: string;
  picture?: string | null;
}

export async function updateUserById(
  userId: string,
  payload: IUpdateUserPayload,
  includeAccountContext = true,
): Promise<IUser> {
  const response = await wretch<IUser>(`${BLACKBOX_URL}users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    headers: Session.getHeaders(includeAccountContext),
  });

  if (isWretchError(response)) {
    throw response.error;
  }

  return response.data;
}
